'use client';

import {
    createContext,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useContext,
    useMemo,
    useState,
} from 'react';

// Color themes based on brand brief
export const themes = {
  trusted: {
    primary: '#2B95E3', // Darker blue
    secondary: '#9B6EE3', // Darker purple
    accent: '#FF9E47', // Darker peach
    text: '#2D3748',
    textMuted: 'rgba(45, 55, 72, 0.7)',
    hover: '#1A75C7',
    gradient: 'linear-gradient(135deg, #2B95E3 0%, #9B6EE3 100%)',
    bgGradient: 'linear-gradient(165deg, #FFFFFF 0%, #F7FAFC 100%)',
    name: 'Trusted & Reliable',
    description: 'For the determined builders',
  },
  creative: {
    primary: '#9B6EE3', // Darker purple
    secondary: '#FF9E47', // Darker peach
    accent: '#2B95E3', // Darker blue
    text: '#2D3748',
    textMuted: 'rgba(45, 55, 72, 0.7)',
    hover: '#8450D1',
    gradient: 'linear-gradient(135deg, #9B6EE3 0%, #FF9E47 100%)',
    bgGradient: 'linear-gradient(165deg, #FFFFFF 0%, #F7FAFC 100%)',
    name: 'Creative & Young',
    description: 'For the bold innovators',
  },
  pioneering: {
    primary: '#FF9E47', // Darker peach
    secondary: '#2B95E3', // Darker blue
    accent: '#9B6EE3', // Darker purple
    text: '#2D3748',
    textMuted: 'rgba(45, 55, 72, 0.7)',
    hover: '#E88A33',
    gradient: 'linear-gradient(135deg, #FF9E47 0%, #2B95E3 100%)',
    bgGradient: 'linear-gradient(165deg, #FFFFFF 0%, #F7FAFC 100%)',
    name: 'Tech Pioneer',
    description: 'For the future shapers',
  },
} as const;

type ThemeContextType = {
  currentTheme: keyof typeof themes;
  setCurrentTheme: Dispatch<SetStateAction<keyof typeof themes>>;
  themes: typeof themes;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentTheme, setCurrentTheme] =
    useState<keyof typeof themes>('trusted');

  const value = useMemo(
    () => ({
      currentTheme,
      setCurrentTheme,
      themes,
    }),
    [currentTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
